/** Trust-row marks in the print brochure's seal style (blue Europe, gold accents). */
import { ld } from "@/lib/theme/light-dark";
import { useTranslations } from "next-intl";
import { type ReactNode, useId } from "react";

import { EUROPE_MAP_PATH, GERMANY_MAP_PATH } from "./map-paths";
import { H } from "./hero-tokens";

const SIZE = 22;
const BLUE = ld("#1450BE", "#7baaff");
const GOLD = ld(H.amber, "#F0DCB0");

/** Europe silhouette in a thin blue ring („Alle Daten in der EU“). */
export function EuropeMapIcon({ size = SIZE }: { readonly size?: number }): ReactNode {
  const t = useTranslations("marketing.heroCompliance");
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" role="img" aria-label={t("alleDatenEu")}>
      <circle cx="24" cy="24" r="22.5" fill="none" stroke={BLUE} strokeWidth="2.6" />
      <path d={EUROPE_MAP_PATH} fill={BLUE} transform="translate(8.6 8.6) scale(0.64)" />
    </svg>
  );
}

/** Germany as a halftone silhouette with three dots in the flag colors (no text, the label says it). */
export function GermanyIcon({ size = SIZE }: { readonly size?: number }): ReactNode {
  const t = useTranslations("marketing.heroCompliance");
  const dotsId = useId();
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" role="img" aria-label={t("entwickeltDeutschland")}>
      <defs>
        <pattern id={dotsId} width="2.4" height="2.4" patternUnits="userSpaceOnUse">
          <circle cx="1.2" cy="1.2" r="0.95" fill={ld("#8F949C", "#80848b")} />
        </pattern>
      </defs>
      <path d={GERMANY_MAP_PATH} fill={ld("rgba(11,15,25,0.04)", "rgba(250,250,250,0.04)")} />
      <path d={GERMANY_MAP_PATH} fill={`url(#${dotsId})`} />
      <circle cx="14.2" cy="34" r="6.6" fill="#0B0F19" stroke={ld("transparent", "rgba(250,250,250,0.35)")} strokeWidth="0.8" />
      <circle cx="24" cy="34" r="6.6" fill="#DD0000" />
      <circle cx="33.8" cy="34" r="6.6" fill="#FFCE00" />
    </svg>
  );
}

/** Shield + lock from the brochure's „Revisionssicher“ mark. */
export function AuditProofIcon({ size = SIZE }: { readonly size?: number }): ReactNode {
  const t = useTranslations("marketing.heroCompliance");
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" role="img" aria-label={t("revisionssichereAblage")}>
      <path
        d="M24 4 L41 8.5 V21.5 c0 11.5 -7 17.8 -17 21.5 C14 39.3 7 33 7 21.5 V8.5 Z"
        fill="none"
        stroke={ld("rgba(11,15,25,0.62)", "rgba(250,250,250,0.7)")}
        strokeWidth="3.4"
        strokeLinejoin="round"
      />
      <rect x="18.5" y="21" width="11" height="9" rx="1.8" fill={GOLD} />
      <path d="M21 21 v-2.8 a3 3 0 0 1 6 0 V21" fill="none" stroke={GOLD} strokeWidth="2.8" strokeLinecap="round" />
    </svg>
  );
}

const STAR =
  "M0,-1 0.225,-0.309 0.951,-0.309 0.363,0.118 0.588,0.809 0,0.382 -0.588,0.809 -0.363,0.118 -0.951,-0.309 -0.225,-0.309Z";
/** Five stars on the top of the ring (−150° … −30°). */
const GOBD_STARS: readonly (readonly [number, number])[] = [-150, -120, -90, -60, -30].map((deg) => {
  const a = (deg * Math.PI) / 180;
  return [24 + 19.3 * Math.cos(a), 24 + 19.3 * Math.sin(a)] as const;
});

/** Own GoBD seal: blue ring with stars, German stripes at the sides, „GoBD“ in the middle (no conformity claim). */
export function RetentionIcon({ size = SIZE }: { readonly size?: number }): ReactNode {
  const t = useTranslations("marketing.heroCompliance");
  const clipId = useId();
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" role="img" aria-label={t("gobdKonformesArbeitenMoeglich")}>
      <defs>
        <clipPath id={clipId}>
          <circle cx="24" cy="24" r="23.5" />
        </clipPath>
      </defs>
      <circle cx="24" cy="24" r="23.5" fill="#1450BE" />
      <g clipPath={`url(#${clipId})`}>
        <rect x="0" y="20.6" width="9" height="2.2" fill="#0B0F19" />
        <rect x="0" y="22.8" width="9" height="2.2" fill="#DD0000" />
        <rect x="0" y="25" width="9" height="2.2" fill="#FFCE00" />
        <rect x="39" y="20.6" width="9" height="2.2" fill="#0B0F19" />
        <rect x="39" y="22.8" width="9" height="2.2" fill="#DD0000" />
        <rect x="39" y="25" width="9" height="2.2" fill="#FFCE00" />
      </g>
      {GOBD_STARS.map(([x, y]) => (
        <path key={x} d={STAR} transform={`translate(${x.toFixed(2)} ${y.toFixed(2)}) scale(2.3)`} fill="#FFCC00" />
      ))}
      <circle cx="24" cy="24" r="15.2" fill="#ffffff" />
      {/* i18n-exempt: name of the German regulation */}
      <text x="24" y="27.6" textAnchor="middle" fontSize="9.8" fontWeight="800" letterSpacing="-0.3" fill="#2A2D33" style={{ fontFamily: H.sans }}>
        GoBD
      </text>
    </svg>
  );
}
